import React, { useState, useEffect } from 'react';
import { firebaseBrokerService } from '../../services/FirebaseBrokerService';
import { firebasePlatformService, Platform } from '../../services/FirebasePlatformService';
import { Broker } from '../../services/BrokerService';
import LeadsManager from './LeadsManager';
import './AdminDashboard.css';

const AdminDashboard: React.FC = () => {
  const [activeView, setActiveView] = useState('overview');
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showBrokerModal, setShowBrokerModal] = useState(false);
  const [editingBroker, setEditingBroker] = useState<Broker | null>(null);
  const [showPlatformModal, setShowPlatformModal] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState<any>(null);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [userActivity, setUserActivity] = useState<any[]>([]);
  const [systemStats, setSystemStats] = useState({
    totalBrokers: 0,
    totalPlatforms: 0,
    activeBrokers: 0,
    totalUsers: 0,
    systemUptime: '7 days',
    storageUsage: '45%'
  });

  // Initialize data
  useEffect(() => {
    loadDashboardData();
    // Also initialize both services to ensure default data exists
    initializeAllData();
  }, []);

  const initializeAllData = async () => {
    try {
      console.log('Initializing all Firebase data...');
      setLoading(true);
      
      // Initialize brokers database
      await firebaseBrokerService.initializeDatabase();
      console.log('Brokers initialized');
      
      // Initialize platforms database  
      await firebasePlatformService.initializeDatabase();
      console.log('Platforms initialized');
      
      // Reload dashboard data to see the changes
      await loadDashboardData();
      
      alert('✅ Default data initialized successfully! Check IB Partners and Platforms pages.');
      console.log('Firebase initialization complete');
    } catch (error) {
      console.error('Failed to initialize Firebase data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert('❌ Failed to initialize data: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      console.log('Loading dashboard data...');
      
      // Load brokers from Firebase and initialize if empty
      const brokersData = await firebaseBrokerService.getAllBrokers();
      console.log('Loaded brokers:', brokersData);
      setBrokers(brokersData);
      
      // Load platforms from Firebase and initialize if empty
      const platformsData = await firebasePlatformService.getAllPlatforms();
      console.log('Loaded platforms:', platformsData);
      setPlatforms(platformsData);
      
      // Generate mock user activity
      const mockActivities = [
        { id: 1, user: 'John Doe', action: 'Added new broker', time: '2 mins ago' },
        { id: 2, user: 'Jane Smith', action: 'Updated platform settings', time: '5 mins ago' },
        { id: 3, user: 'Mike Johnson', action: 'Deleted broker', time: '10 mins ago' },
        { id: 4, user: 'Sarah Wilson', action: 'Updated system config', time: '15 mins ago' },
        { id: 5, user: 'Tom Brown', action: 'Uploaded new platform', time: '20 mins ago' }
      ];
      setUserActivity(mockActivities);
      
      // Calculate stats
      setSystemStats({
        totalBrokers: brokersData.length,
        totalPlatforms: platformsData.length,
        activeBrokers: brokersData.filter(b => b.isActive).length,
        totalUsers: mockActivities.length,
        systemUptime: '7 days',
        storageUsage: '45%'
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Broker management
  const [brokerForm, setBrokerForm] = useState({
    name: '',
    description: '',
    minDeposit: '',
    leverage: '',
    spreads: '',
    regulation: '',
    website: '',
    logo: '',
    features: [] as string[],
    rating: 0,
    tradingPlatforms: [] as string[],
    accountTypes: [] as string[],
    contactEmail: '',
    referralLink: '',
    isActive: true as boolean
  });

  const handleBrokerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare broker data ensuring all fields match the Broker interface
      const brokerData = {
        name: brokerForm.name,
        description: brokerForm.description,
        minDeposit: brokerForm.minDeposit,
        leverage: brokerForm.leverage,
        spreads: brokerForm.spreads,
        regulation: brokerForm.regulation.split(',').map(r => r.trim()).filter(r => r),
        website: brokerForm.website,
        logo: brokerForm.logo,
        features: brokerForm.features.filter(f => f), // Remove empty strings
        rating: parseFloat(brokerForm.rating.toString()) || 0,
        tradingPlatforms: brokerForm.tradingPlatforms.filter(p => p), // Remove empty strings
        accountTypes: brokerForm.accountTypes.filter(a => a), // Remove empty strings
        contactEmail: brokerForm.contactEmail,
        referralLink: brokerForm.referralLink,
        isActive: brokerForm.isActive
      };

      if (editingBroker) {
        await firebaseBrokerService.updateBroker(editingBroker.id, brokerData);
      } else {
        await firebaseBrokerService.addBroker(brokerData);
      }

      await loadDashboardData();
      resetBrokerForm();
      setShowBrokerModal(false);
    } catch (error) {
      console.error('Error saving broker:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetBrokerForm = () => {
    setBrokerForm({
      name: '',
      description: '',
      minDeposit: '',
      leverage: '',
      spreads: '',
      regulation: '',
      website: '',
      logo: '',
      features: [],
      rating: 0,
      tradingPlatforms: [],
      accountTypes: [],
      contactEmail: '',
      referralLink: '',
      isActive: true
    });
    setEditingBroker(null);
  };

  const openBrokerModal = (broker?: Broker) => {
    if (broker) {
      setBrokerForm({
        name: broker.name,
        description: broker.description,
        minDeposit: broker.minDeposit,
        leverage: broker.leverage,
        spreads: broker.spreads,
        regulation: Array.isArray(broker.regulation) ? broker.regulation.join(', ') : broker.regulation,
        website: broker.website || '',
        logo: broker.logo || '',
        isActive: broker.isActive,
        features: Array.isArray(broker.features) ? broker.features : [],
        rating: broker.rating,
        tradingPlatforms: Array.isArray(broker.tradingPlatforms) ? broker.tradingPlatforms : [],
        accountTypes: Array.isArray(broker.accountTypes) ? broker.accountTypes : [],
        contactEmail: broker.contactEmail || '',
        referralLink: broker.referralLink || ''
      });
      setEditingBroker(broker);
    } else {
      resetBrokerForm();
    }
    setShowBrokerModal(true);
  };

  const deleteBroker = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this broker?')) {
      setLoading(true);
      try {
        await firebaseBrokerService.deleteBroker(id);
        await loadDashboardData();
      } catch (error) {
        console.error('Error deleting broker:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleBrokerStatus = async (id: string) => {
    setLoading(true);
    try {
      await firebaseBrokerService.toggleBrokerStatus(id);
      await loadDashboardData();
    } catch (error) {
      console.error('Error toggling broker status:', error);
    } finally {
      setLoading(false);
    }
  };

  // Platform management
  const [platformForm, setPlatformForm] = useState({
    name: '',
    description: '',
    type: 'Desktop' as Platform['type'],
    downloadLink: '',
    website: '',
    logo: '',
    features: [] as string[],
    operatingSystem: [] as string[],
    languages: [] as string[],
    minSystemRequirements: '',
    developer: '',
    version: '',
    fileSize: '',
    price: '',
    license: '',
    category: 'Trading Platform' as Platform['category'],
    popularity: 1,
    lastUpdated: '',
    isActive: true
  });

  const handlePlatformSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const platformData = {
        name: platformForm.name,
        description: platformForm.description,
        type: platformForm.type,
        downloadLink: platformForm.downloadLink,
        website: platformForm.website,
        logo: platformForm.logo,
        features: platformForm.features.filter(f => f),
        operatingSystem: platformForm.operatingSystem.filter(os => os),
        languages: platformForm.languages.filter(l => l),
        minSystemRequirements: platformForm.minSystemRequirements,
        developer: platformForm.developer,
        version: platformForm.version,
        fileSize: platformForm.fileSize,
        price: platformForm.price,
        license: platformForm.license,
        category: platformForm.category,
        popularity: platformForm.popularity,
        lastUpdated: new Date().toISOString(),
        isActive: platformForm.isActive
      };

      if (editingPlatform) {
        await firebasePlatformService.updatePlatform(editingPlatform.id, platformData);
      } else {
        await firebasePlatformService.addPlatform(platformData);
      }

      await loadDashboardData();
      resetPlatformForm();
      setShowPlatformModal(false);
    } catch (error) {
      console.error('Error saving platform:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetPlatformForm = () => {
    setPlatformForm({
      name: '',
      description: '',
      type: 'Desktop',
      downloadLink: '',
      website: '',
      logo: '',
      features: [],
      operatingSystem: [],
      languages: [],
      minSystemRequirements: '',
      developer: '',
      version: '',
      fileSize: '',
      price: '',
      license: '',
      category: 'Trading Platform',
      popularity: 1,
      lastUpdated: '',
      isActive: true
    });
    setEditingPlatform(null);
  };

  const openPlatformModal = (platform?: Platform) => {
    if (platform) {
      setPlatformForm({
        name: platform.name,
        description: platform.description,
        type: platform.type,
        downloadLink: platform.downloadLink || '',
        website: platform.website,
        logo: platform.logo,
        features: Array.isArray(platform.features) ? platform.features : [],
        operatingSystem: Array.isArray(platform.operatingSystem) ? platform.operatingSystem : [],
        languages: Array.isArray(platform.languages) ? platform.languages : [],
        minSystemRequirements: platform.minSystemRequirements || '',
        developer: platform.developer,
        version: platform.version || '',
        fileSize: platform.fileSize || '',
        price: platform.price,
        license: platform.license,
        category: platform.category,
        popularity: platform.popularity,
        lastUpdated: platform.lastUpdated,
        isActive: platform.isActive
      });
      setEditingPlatform(platform);
    } else {
      resetPlatformForm();
    }
    setShowPlatformModal(true);
  };

  const deletePlatform = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this platform?')) {
      setLoading(true);
      try {
        await firebasePlatformService.deletePlatform(id);
        await loadDashboardData();
      } catch (error) {
        console.error('Error deleting platform:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const togglePlatformStatus = async (id: string) => {
    setLoading(true);
    try {
      await firebasePlatformService.togglePlatformStatus(id);
      await loadDashboardData();
    } catch (error) {
      console.error('Error toggling platform status:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter brokers based on search term
  const filteredBrokers = brokers.filter(broker =>
    broker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    broker.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    broker.features.some(feature => feature.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Filter platforms based on search term
  const filteredPlatforms = platforms.filter(platform =>
    platform.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    platform.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-dashboard">
      <div className="admin-sidebar">
        <div className="admin-logo">
          <h2>UPM Admin</h2>
        </div>
        <nav className="admin-nav">
          <button 
            className={activeView === 'overview' ? 'nav-item active' : 'nav-item'}
            onClick={() => setActiveView('overview')}
          >
            📊 Overview
          </button>
          <button 
            className={activeView === 'brokers' ? 'nav-item active' : 'nav-item'}
            onClick={() => setActiveView('brokers')}
          >
            🏢 IB Partners
          </button>
          <button 
            className={activeView === 'platforms' ? 'nav-item active' : 'nav-item'}
            onClick={() => setActiveView('platforms')}
          >
            💻 Platforms
          </button>
          <button 
            className={activeView === 'leads' ? 'nav-item active' : 'nav-item'}
            onClick={() => setActiveView('leads')}
          >
            👥 Leads Manager
          </button>
          <button 
            className={activeView === 'analytics' ? 'nav-item active' : 'nav-item'}
            onClick={() => setActiveView('analytics')}
          >
            📈 Analytics
          </button>
          <button 
            className={activeView === 'system' ? 'nav-item active' : 'nav-item'}
            onClick={() => setActiveView('system')}
          >
            ⚙️ System
          </button>
        </nav>
      </div>

      <div className="admin-main">
        <div className="admin-header">
          <h1>
            {activeView === 'overview' && 'Dashboard Overview'}
            {activeView === 'brokers' && 'IB Partners Management'}
            {activeView === 'platforms' && 'Platform Management'}
            {activeView === 'leads' && 'Leads Management'}
            {activeView === 'analytics' && 'Analytics & Reports'}
            {activeView === 'system' && 'System Configuration'}
          </h1>
        </div>

        <div className="admin-content">
          {activeView === 'overview' && (
            <div className="admin-overview">
              <div className="dashboard-grid">
                <div className="dashboard-card">
                  <h3>Total Brokers</h3>
                  <div className="stat-number">{systemStats.totalBrokers}</div>
                  <p>Manage your broker partnerships</p>
                  <button 
                    className="dashboard-btn"
                    onClick={() => setActiveView('brokers')}
                  >
                    Manage Brokers
                  </button>
                </div>
                
                <div className="dashboard-card">
                  <h3>Active Brokers</h3>
                  <div className="stat-number">{systemStats.activeBrokers}</div>
                  <p>Currently active partnerships</p>
                  <button 
                    className="dashboard-btn"
                    onClick={() => setActiveView('brokers')}
                  >
                    View Active
                  </button>
                </div>
                
                <div className="dashboard-card">
                  <h3>Platforms</h3>
                  <div className="stat-number">{systemStats.totalPlatforms}</div>
                  <p>Available trading platforms</p>
                  <button 
                    className="dashboard-btn"
                    onClick={() => setActiveView('platforms')}
                  >
                    Manage Platforms
                  </button>
                </div>
                
                <div className="dashboard-card">
                  <h3>System Status</h3>
                  <div className="stat-number">✓</div>
                  <p>Operational for {systemStats.systemUptime}</p>
                  <button 
                    className="dashboard-btn"
                    onClick={() => setActiveView('system')}
                  >
                    System Health
                  </button>
                </div>
              </div>

              <div className="recent-activity">
                <h3>Recent Activity</h3>
                <div className="activity-list">
                  {userActivity.slice(0, 5).map(activity => (
                    <div key={activity.id} className="activity-item">
                      <div className="activity-text">
                        <strong>{activity.user}</strong> {activity.action}
                      </div>
                      <div className="activity-time">{activity.time}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeView === 'brokers' && (
            <div className="admin-section">
              <div className="section-header">
                <h2>Broker Management</h2>
                <div className="header-actions">
                  <input
                    type="text"
                    placeholder="Search brokers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  <button 
                    onClick={() => openBrokerModal()}
                    className="btn-primary"
                  >
                    Add Broker
                  </button>
                </div>
              </div>

              <div className="data-table">
                <div className="table-header">
                  <div className="table-cell">Logo</div>
                  <div className="table-cell">Name</div>
                  <div className="table-cell">Min Deposit</div>
                  <div className="table-cell">Leverage</div>
                  <div className="table-cell">Rating</div>
                  <div className="table-cell">Status</div>
                  <div className="table-cell">Actions</div>
                </div>
                
                {loading ? (
                  <div className="loading-state">Loading brokers...</div>
                ) : filteredBrokers.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">🏢</div>
                    <h3>No brokers found</h3>
                    <p>Get started by adding your first broker</p>
                    <button 
                      onClick={() => openBrokerModal()}
                      className="add-first-broker-btn"
                    >
                      Add First Broker
                    </button>
                  </div>
                ) : (
                  filteredBrokers.map(broker => (
                    <div key={broker.id} className="table-row">
                      <div className="table-cell">
                        <img src={broker.logo} alt={broker.name} className="broker-logo-small" />
                      </div>
                      <div className="table-cell">
                        <div className="broker-name">{broker.name}</div>
                        <div className="broker-regulated">{broker.regulation.join(', ')}</div>
                      </div>
                      <div className="table-cell">${broker.minDeposit}</div>
                      <div className="table-cell">{broker.leverage}</div>
                      <div className="table-cell">⭐ {broker.rating}</div>
                      <div className="table-cell">
                        <span className={`status-badge ${broker.isActive ? 'active' : 'inactive'}`}>
                          {broker.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="table-cell">
                        <div className="action-buttons">
                          <button 
                            onClick={() => openBrokerModal(broker)}
                            className="btn-edit"
                          >
                            Edit
                          </button>
                          <button 
                            onClick={() => toggleBrokerStatus(broker.id)}
                            className={`btn-toggle ${broker.isActive ? 'btn-activate' : 'btn-deactivate'}`}
                          >
                            {broker.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button 
                            onClick={() => deleteBroker(broker.id)}
                            className="btn-delete"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeView === 'platforms' && (
            <div className="admin-section">
              <div className="section-header">
                <h2>Platform Management</h2>
                <div className="header-actions">
                  <input
                    type="text"
                    placeholder="Search platforms..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  <button 
                    onClick={() => openPlatformModal()}
                    className="btn-primary"
                  >
                    Add Platform
                  </button>
                </div>
              </div>

              <div className="data-table">
                <div className="table-header">
                  <div className="table-cell">Logo</div>
                  <div className="table-cell">Name</div>
                  <div className="table-cell">Description</div>
                  <div className="table-cell">Status</div>
                  <div className="table-cell">Actions</div>
                </div>
                
                {loading ? (
                  <div className="loading-state">Loading platforms...</div>
                ) : filteredPlatforms.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">💻</div>
                    <h3>No platforms found</h3>
                    <p>Get started by adding your first trading platform</p>
                    <button 
                      onClick={() => openPlatformModal()}
                      className="add-first-broker-btn"
                    >
                      Add First Platform
                    </button>
                  </div>
                ) : (
                  filteredPlatforms.map(platform => (
                    <div key={platform.id} className="table-row">
                      <div className="table-cell">
                        {platform.logo ? (
                          <img src={platform.logo} alt={platform.name} className="broker-logo-small" />
                        ) : (
                          <div className="broker-logo-placeholder">📱</div>
                        )}
                      </div>
                      <div className="table-cell">
                        <div className="broker-name">{platform.name}</div>
                      </div>
                      <div className="table-cell">{platform.description.substring(0, 50)}...</div>
                      <div className="table-cell">
                        <span className={`status-badge ${platform.isActive ? 'active' : 'inactive'}`}>
                          {platform.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="table-cell">
                        <div className="action-buttons">
                          <button 
                            onClick={() => openPlatformModal(platform)}
                            className="btn-edit"
                          >
                            Edit
                          </button>
                          <button 
                            onClick={() => togglePlatformStatus(platform.id)}
                            className={`btn-toggle ${platform.isActive ? 'btn-activate' : 'btn-deactivate'}`}
                          >
                            {platform.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button 
                            onClick={() => deletePlatform(platform.id)}
                            className="btn-delete"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeView === 'leads' && (
            <div className="admin-section">
              <LeadsManager />
            </div>
          )}

          {activeView === 'analytics' && (
            <div className="admin-section">
              <div className="section-header">
                <h2>Analytics & Reports</h2>
              </div>
              <div className="analytics-content">
                <div className="analytics-grid">
                  <div className="analytics-card">
                    <h3>Broker Performance</h3>
                    <p>Track broker performance metrics and user engagement</p>
                    <div className="analytics-chart">
                      <div className="chart-placeholder">📈</div>
                      <p>Performance Chart</p>
                    </div>
                  </div>
                  
                  <div className="analytics-card">
                    <h3>User Engagement</h3>
                    <p>Analyze user interactions with broker platforms</p>
                    <div className="analytics-chart">
                      <div className="chart-placeholder">📊</div>
                      <p>Engagement Metrics</p>
                    </div>
                  </div>
                  
                  <div className="analytics-card">
                    <h3>Conversion Rates</h3>
                    <p>Track conversion from referral clicks to signups</p>
                    <div className="analytics-chart">
                      <div className="chart-placeholder">🎯</div>
                      <p>Conversion Analytics</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeView === 'system' && (
            <div className="admin-section">
              <div className="section-header">
                <h2>System Configuration</h2>
              </div>
              <div className="system-config">
                <div className="config-card">
                  <h3>System Status</h3>
                  <div className="config-content">
                    <div className="config-item">
                      <span>Uptime</span>
                      <span>{systemStats.systemUptime}</span>
                    </div>
                    <div className="config-item">
                      <span>Storage Usage</span>
                      <span>{systemStats.storageUsage}</span>
                    </div>
                    <div className="config-item">
                      <span>Total Brokers</span>
                      <span>{systemStats.totalBrokers}</span>
                    </div>
                  </div>
                </div>
                
                <div className="config-card">
                  <h3>Database Management</h3>
                  <div className="config-content">
                    <button 
                      className="maintenance-btn"
                      onClick={initializeAllData}
                      style={{ marginBottom: '10px' }}
                    >
                      Initialize Default Data
                    </button>
                    <p style={{ fontSize: '12px', color: '#888' }}>
                      Click to ensure brokers and platforms are loaded
                    </p>
                  </div>
                </div>
                
                <div className="config-card">
                  <h3>Maintenance</h3>
                  <div className="config-content">
                    <button className="maintenance-btn">Clear Cache</button>
                    <button className="maintenance-btn">Backup Data</button>
                    <button className="maintenance-btn">Export Brokers</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Broker Modal */}
      {showBrokerModal && (
        <div className="modal-overlay" onClick={() => setShowBrokerModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingBroker ? 'Edit Broker' : 'Add New Broker'}</h2>
              <button 
                onClick={() => setShowBrokerModal(false)}
                className="close-btn"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleBrokerSubmit} className="broker-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Broker Name</label>
                  <input
                    type="text"
                    value={brokerForm.name}
                    onChange={(e) => setBrokerForm({...brokerForm, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Website URL</label>
                  <input
                    type="url"
                    value={brokerForm.website}
                    onChange={(e) => setBrokerForm({...brokerForm, website: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={brokerForm.description}
                  onChange={(e) => setBrokerForm({...brokerForm, description: e.target.value})}
                  rows={3}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Min Deposit ($)</label>
                  <input
                    type="text"
                    value={brokerForm.minDeposit}
                    onChange={(e) => setBrokerForm({...brokerForm, minDeposit: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Leverage</label>
                  <input
                    type="text"
                    value={brokerForm.leverage}
                    onChange={(e) => setBrokerForm({...brokerForm, leverage: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Spreads</label>
                  <input
                    type="text"
                    value={brokerForm.spreads}
                    onChange={(e) => setBrokerForm({...brokerForm, spreads: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Regulation (comma separated)</label>
                  <input
                    type="text"
                    value={brokerForm.regulation}
                    onChange={(e) => setBrokerForm({...brokerForm, regulation: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Logo URL</label>
                <input
                  type="url"
                  value={brokerForm.logo}
                  onChange={(e) => setBrokerForm({...brokerForm, logo: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Contact Email</label>
                <input
                  type="email"
                  value={brokerForm.contactEmail}
                  onChange={(e) => setBrokerForm({...brokerForm, contactEmail: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Referral Link</label>
                <input
                  type="url"
                  value={brokerForm.referralLink}
                  onChange={(e) => setBrokerForm({...brokerForm, referralLink: e.target.value})}
                />
              </div>

              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={brokerForm.isActive}
                    onChange={(e) => setBrokerForm({...brokerForm, isActive: e.target.checked})}
                  />
                  Active
                </label>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => setShowBrokerModal(false)}
                  className="cancel-btn"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="save-btn"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : (editingBroker ? 'Update' : 'Add')} Broker
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Platform Modal */}
      {showPlatformModal && (
        <div className="modal-overlay" onClick={() => setShowPlatformModal(false)}>
          <div className="modal-content platform-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingPlatform ? 'Edit Platform' : 'Add New Platform'}</h2>
              <button 
                onClick={() => setShowPlatformModal(false)}
                className="close-btn"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handlePlatformSubmit} className="platform-form">
              <div className="form-group">
                <label>Platform Name</label>
                <input
                  type="text"
                  value={platformForm.name}
                  onChange={(e) => setPlatformForm({...platformForm, name: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={platformForm.description}
                  onChange={(e) => setPlatformForm({...platformForm, description: e.target.value})}
                  rows={3}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Logo URL</label>
                  <input
                    type="url"
                    value={platformForm.logo}
                    onChange={(e) => setPlatformForm({...platformForm, logo: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Website URL</label>
                  <input
                    type="url"
                    value={platformForm.website}
                    onChange={(e) => setPlatformForm({...platformForm, website: e.target.value})}
                  />
                </div>
              </div>

              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={platformForm.isActive}
                    onChange={(e) => setPlatformForm({...platformForm, isActive: e.target.checked})}
                  />
                  Active
                </label>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => setShowPlatformModal(false)}
                  className="cancel-btn"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="save-btn"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : (editingPlatform ? 'Update' : 'Add')} Platform
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;